import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Icon from "../../../components/AppIcon";

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("vision");
  const videoRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (videoRef.current) {
            videoRef.current.play(); // autoplay when visible
          }
        } else {
          if (videoRef.current) {
            videoRef.current.pause(); // pause when out of view
          }
        }
      },
      { threshold: 0.4 }
    );

    const element = document.getElementById("about-video");
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      className="py-20 bg-gradient-to-b from-background to-surface"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            <span>
                  <span className="text-nipix-blue">A</span>
                  <span className="text-text-primary">bout </span>
                  <span className="text-nipix-blue">N</span>
                  <span className="text-text-primary">ipix </span>
                  <span className="text-nipix-blue">T</span>
                  <span className="text-text-primary">echnology</span>
                </span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl sm:max-w-4xl lg:max-w-5xl mx-auto leading-relaxed">
            Nipix Technology is an innovative education technology company committed to shaping the next generation of thinkers, builders, and change makers. 
            Through our unique blend of reverse learning, real-world problem solving, and hands-on project execution, we empower learners to go beyond theory and truly innovate.
            At the core of our philosophy is the <span className="text-nipix-blue font-bold"> "Build → Break → Understand → Innovate" </span> model—a learner-first approach that emphasizes creation over consumption. 
            This method ensures every learner actively engages with technology, grasps the fundamentals by experimentation, and ultimately develops solutions that create real-world impact.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-8 bg-muted rounded-lg p-1">
              <button
                onClick={() => setActiveTab("vision")}
                className={`flex-1 py-3 px-6 rounded-md font-semibold transition-all duration-300 ${
                  activeTab === "vision"
                    ? "bg-white text-primary shadow-md"
                    : "text-text-secondary hover:text-text-primary"
                }`}
              >
                <Icon name="Eye" size={16} className="inline mr-2" strokeWidth={2} />
                Vision
              </button>
              <button
                onClick={() => setActiveTab("mission")}
                className={`flex-1 py-3 px-6 rounded-md font-semibold transition-all duration-300 ${
                  activeTab === "mission"
                    ? "bg-white text-primary shadow-md"
                    : "text-text-secondary hover:text-text-primary"
                }`}
              >
                <Icon name="Target" size={16} className="inline mr-2" strokeWidth={2} />
                Mission
              </button>
            </div>

            {/* Tab Content */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-border"
            >
              {activeTab === "vision" ? (
                <>
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                      <Icon name="Eye" size={24} className="text-primary" strokeWidth={2} />
                    </div>
                    <h3 className="text-2xl font-bold text-text-primary">Our Vision</h3>
                  </div>
                  <p className="text-lg text-text-secondary leading-relaxed">
                    To become a global leader in innovative education technology, 
                    where every student and professional — regardless of background or academic stream — can transform 
                    ideas into impactful real-world innovations through 
                    real-time project-based learning and industry-ready solutions.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                      <Icon name="Target" size={24} className="text-secondary" strokeWidth={2} />
                    </div>
                    <h3 className="text-2xl font-bold text-text-primary">Our Mission</h3>
                  </div>
                  <p className="text-lg text-text-secondary leading-relaxed">
                    Nipix Technology aims to inspire students to become creators, 
                    not just consumers — by bridging the gap between academic learning and real-world skills through hands-on training, 
                    internships, and product development. Our mission is to deliver high-quality tech education that fosters creativity, 
                    innovation, and entrepreneurial thinking, while nurturing a strong community of learners and 
                    future leaders through mentorship and practical experience.
                  </p>
                </>
              )}
            </motion.div>
          </motion.div>

          {/* Visual Side → Clean Auto-Play Video */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div id="about-video" className="relative">
              <div className="bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl p-6 group">
                <video
                  ref={videoRef}
                  src="/nipix technology intro.mp4"  // ✅ replace with actual video path
                  muted
                  loop
                  playsInline
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
